<script setup>
import {onMounted, ref, defineProps, defineEmits, inject, watch, defineExpose} from "vue";
import { initializeMapBoundary } from "@/components/MapPage/MainMap/initializeMapBoundary";
import { initializeMap } from "@/components/MapPage/MainMap/initializeMap";
import axios from "axios";
import {initializeNews} from "@/components/MapPage/MainMap/initializeNews";
import {useMapStore} from "@/store/Map/map-store";
import {useLK} from "@/controllerLK/LKstore";
import {initializeMapRating} from "@/components/MapPage/MainMap/initializeMapRating";
import {initializeMapHandlers, removeListener} from "@/components/MapPage/MainMap/initializeMapHandlers";
import ZoomInIcon from "@/components/Icons/ZoomInIcon.vue";
import ZoomOutIcon from "@/components/Icons/ZoomOutIcon.vue";
import { t } from "@/controllerLK/GlobusLocalization";

const prop = defineProps({
  map: {
    type: [Object, null],
    required: true,
  },
  filterKey: {
    type: Number,
    required: true,
    default: () => 0
  },
  isNewsListOpened: {
    type: Boolean,
    default: false,
    required: true
  },
});
const emit = defineEmits(["openNewsList", "updateFilter"]);

const LKStore = useLK();
const mapStore = useMapStore();

const mapRef = ref(null);
const getNewsError = ref(false);

const selectedDateFilter = inject("selectedDateFilter");
const selectedColorList = inject("selectedColorList");
const selectedTheme = inject("selectedTheme");
const selectedRubric = inject("selectedRubric");
const selectedSentimentFilter = inject("selectedSentimentFilter");

const filterKey = inject("filterKey");

const hideVectorFlower = () => {
  let vectorFlowerPoint;
  mapRef.value.getLayers().forEach((layer) => {
    if (layer.getSource().get("name") === "flower") vectorFlowerPoint = layer.getSource();
  });
  vectorFlowerPoint.clear();
  removeListener();
};

defineExpose({hideVectorFlower});

watch(filterKey, async () => {
  const filters = getFilters();

  hideVectorFlower();

  try {
    // Загрузка списка новостей и рейтинга для регионов
    await mapStore.getMapNewsList(LKStore.project.id, prop.map.name, selectedDateFilter.value, filters);

    // Раскрашивание регионов по рейтингу
    initializeMapRating(mapRef.value, mapStore.mapRatingList);

    // Добавление точек с новостями
    initializeNews(mapRef.value, mapStore.mapNewsList);
  } catch (error) {
    if (error.response && error.response.status >= 500 && error.response.status <= 599) {
      getNewsError.value = true;
    }
    console.error("Error loading map news:", error);
  }
});

onMounted(async () => {
  try {
    // Инициализация open layers
    mapRef.value = initializeMap();

    const filters = getFilters();

    // Инициализация хенлдеров на клик/ховер
    initializeMapHandlers(mapRef.value, (newsList) => { emit("openNewsList", newsList); });

    // Загрузка регионов и построение их на карте
    initializeMapBoundary(mapRef.value, await getBoundaries(), prop.map);

    try {
      // Загрузка списка новостей и рейтинга для регионов
      await mapStore.getMapNewsList(LKStore.project.id, prop.map.name, selectedDateFilter.value, filters);

      // Раскрашивание регионов по рейтингу
      initializeMapRating(mapRef.value, mapStore.mapRatingList);

      // Добавление точек с новостями
      initializeNews(mapRef.value, mapStore.mapNewsList);
    } catch (error) {
      if (error.response && error.response.status >= 500 && error.response.status <= 599) {
        getNewsError.value = true;
      }
      console.error("Error loading map news:", error);
    }

  } catch (e) {
    console.log(e);
  }

});

const getFilters = () => {
  return {
    blocksIds: selectedTheme.value.map((item) => item.id),
    rubricsIds: selectedRubric.value.map((item) => item.id),
    sentiment: selectedSentimentFilter.value.map((item) => item.value),
    importanceValues: selectedColorList.value,
  };
};

const getBoundaries = async () => {
  try {
    const mapFilePath = prop.map.map_file_path;
    const url = `${mapFilePath}`;
    return await axios.get(url).then((res) => res.data.features);
  } catch (e) {
    console.log(e);
  }
};

const zoomTo = (amount) => {
  const view = mapRef.value.getView();
  const zoom = view.getZoom();
  view.animate({zoom: zoom + amount});
};

</script>
<template>
  <div
    v-if="mapStore.isMapLoading"
    class="map-loader"
  >
    <div class="loader" />
  </div>
  <div
    id="map"
    class="map"
  />
  <div
    class="custom-zoom"
    :class="{ 'custom-zoom--aside': isNewsListOpened }"
  >
    <div
      class="custom-zoom__item"
      @click="zoomTo(+1)"
    >
      <ZoomInIcon />
    </div>
    <div
      class="custom-zoom__item"
      @click="zoomTo(-1)"
    >
      <ZoomOutIcon />
    </div>
  </div>
  <Modal 
    v-if="getNewsError" 
    :title="t('map_page.error.no_news')" 
    :confirm-text="t('map_page.button.reload')"
    :cancel-text="t('map_page.button.cancel')"
    teleport="#app"
    @close="getNewsError = false" 
    @cancel="getNewsError = false" 
    @confirm="getNewsError = false; emit('updateFilter')" 
  >
    {{ t('map_page.error.reload') }}
  </Modal>
</template>
<style lang="scss" scoped>
.custom-zoom {
  display: flex;
  gap: 5px;
  left: 50%;
  transform: translateX(-50%);
  justify-content: center;
  position: absolute;
  bottom: 14px;

  &--aside {
    left: 40px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
    font-size: 16px;
    font-weight: 600;
    border-radius: 8px;
    color: #212327;
    background: #FFF;
    box-shadow: 0 4px 4px 0 #0000000F;
    border: 0.5px solid #E7E7E7;
    width: 36px;
    height: 36px;
  }
}

.map {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
 /* background: linear-gradient(124.99deg, #ECF7FF 12.37%, rgba(235, 246, 255, 0.86) 82.72%); */
}

#popup {
  border-radius: 6px;
  padding: 10px 8px;
  color: black;
  max-width: 300px;
  min-width: 100px;
  position: relative;
  z-index: 3;
}

.map-loader {
  font-weight: 500;
  position: fixed;
  z-index: 1;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #06a2ff;
  gap: 10px;
  backdrop-filter: blur(13px);
}

.loader {
  position: relative;
  display: block;
  width: 44px;
  height: 44px;
  border-radius: 50%;

}
.loader::before {
  position: absolute;
  box-sizing: border-box;
  content: "";

  animation: prixClipFix 2s linear infinite ;
  border: 3px solid #7B87A5;
  border-radius: 50%;
  inset: 0;
}


@keyframes prixClipFix {
  0%   {clip-path:polygon(50% 50%,0 0,0 0,0 0,0 0,0 0)}
  25%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 0,100% 0,100% 0)}
  50%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,100% 100%,100% 100%)}
  75%  {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,0 100%,0 100%)}
  100% {clip-path:polygon(50% 50%,0 0,100% 0,100% 100%,0 100%,0 0)}
}
</style>
